import {
  type EditorState,
  type Extension,
  Prec,
  type StateField,
  type Text,
} from '@codemirror/state';
import {
  type Command,
  EditorView,
  type ViewUpdate,
  keymap,
  ViewPlugin,
} from '@codemirror/view';
import {insertTab} from '@codemirror/commands';
import type {EmmetKnownSyntax} from '@emmetio/codemirror6-plugin';
import {cssLanguage} from '@codemirror/lang-css';

/**
 * Maps CodeMirror language names to Emmet syntax identifiers.
 * Only languages that support Emmet abbreviations are included.
 */
const emmetSyntaxMap: Record<string, string> = {
  HTML: 'html',
  HTMLBARS: 'html', // Code Block uses 'htmlbars' as the key for HTML
  CSS: 'css',
  JSX: 'jsx',
  TSX: 'jsx',
};

/**
 * Returns true if the given tag name is a standard HTML element.
 * Uses the DOM: `document.createElement` returns an HTMLUnknownElement
 * for unrecognized tags, so this stays current with the browser's
 * supported elements without maintaining a hardcoded list.
 */
function isKnownHtmlTag(tag: string): boolean {
  return !(document.createElement(tag) instanceof HTMLUnknownElement);
}

/**
 * Returns true when a markup (HTML/JSX) Emmet expansion preview looks
 * useful — i.e. the first tag in the output is a known HTML element or
 * a custom element (contains a hyphen, per the Web Components spec).
 *
 * This prevents bare CSS abbreviations like `m10` from being expanded
 * to `<m10></m10>` in HTML mode.  Emmet aliases like `bq` still work
 * because the *preview* contains the resolved tag name (`<blockquote>`).
 */
export function isUsefulMarkupExpansion(preview: string): boolean {
  const match = preview.match(/^<([a-zA-Z][a-zA-Z0-9:-]*)/);
  if (!match?.[1]) return true; // Non-tag output (e.g. doctype) — allow
  const tag = match[1].toLowerCase();
  if (tag.includes('-')) return true; // Custom element (web component)
  return isKnownHtmlTag(tag);
}

/**
 * Returns the Emmet syntax for a given CodeMirror language name,
 * or undefined if Emmet doesn't apply to that language.
 */
export function getEmmetSyntax(language?: string): string | undefined {
  if (!language) return undefined;
  // Support both 'HTML' (LanguageName) and 'html' (SUPPORTED_LANGUAGES key)
  return emmetSyntaxMap[language] ?? emmetSyntaxMap[language.toUpperCase()];
}

/**
 * Returns true when the cursor is inside a CSS context within an HTML document
 * (e.g. inside a `<style>` element or a `style="..."` attribute).
 *
 * This uses our own import of `cssLanguage` from `@codemirror/lang-css`, which
 * shares the same module instance as `@codemirror/lang-html`'s nested CSS parser.
 * The emmetio plugin's bundled `cssLanguage` may be a different instance due to
 * ESM/CJS module resolution, causing its `isActiveAt` checks to fail — so we
 * provide our own check here.
 */
function isCSSContextAt(state: EditorState, pos: number): boolean {
  return cssLanguage.isActiveAt(state, pos);
}

/**
 * Returns true when the tracked abbreviation range abuts `{{ }}` token syntax.
 * Checks two characters before and after the range to catch tokens that the
 * tracker partially overlaps.
 */
function isNearTokenSyntax(
  doc: Text,
  range: {from: number; to: number}
): boolean {
  const from = Math.max(0, range.from - 2);
  const to = Math.min(doc.length, range.to + 2);
  const text = doc.sliceString(from, to);
  return text.includes('{{') || text.includes('}}');
}

/**
 * Creates Emmet extensions for a given language.
 *
 * Includes:
 * - abbreviationTracker: Detects abbreviations as you type and shows a preview.
 *   Press Tab to expand, Esc to dismiss.
 * - Keybindings for common Emmet actions (balance, wrap, tag manipulation, etc.)
 *
 * Returns an empty array for languages that don't support Emmet (e.g. JSON, ENV).
 */
export async function createEmmetExtensions(
  language?: string
): Promise<Extension[]> {
  const syntax = getEmmetSyntax(language);
  if (!syntax) {
    return [];
  }

  const [emmet, emmetCore] = await Promise.all([
    import('@emmetio/codemirror6-plugin'),
    import('emmet'),
  ]);

  const isMarkup = syntax === 'html' || syntax === 'jsx';

  const trackerExts = emmet.abbreviationTracker({
    syntax: syntax as EmmetKnownSyntax,
  });

  // The first extension returned by abbreviationTracker() is the tracker
  // state field.  We read it at Prec.high to decide whether to let the
  // tracker's own Tab handler (at default priority) fire or to fall
  // through to insertTab.
  const trackerStateField = trackerExts[0] as StateField<{
    type: string;
    inactive: boolean;
    range: {from: number; to: number};
    abbreviation: string;
    preview?: string;
  } | null>;

  // Prec.high Tab handler — fires BEFORE the tracker's default-priority Tab.
  //
  // When the tracker is active:
  //  - error type → insert tab
  //  - markup mode + unknown HTML tag in preview → insert tab
  //  - valid abbreviation → return false so the tracker expands it
  //
  // When no tracker is active (Code Block — the abbreviation tracker's
  // htmlLanguage.isActiveAt / cssLanguage.isActiveAt checks fail due to
  // ESM/CJS module resolution), fall back to the standalone
  // expandAbbreviation command.  For markup mode, validate the result
  // against known HTML tags before accepting it.
  const safeEmmetTab: Command = (view) => {
    const tracker = view.state.field(trackerStateField, false);

    if (tracker && !tracker.inactive) {
      if (tracker.type === 'error') {
        insertTab(view);
        return true;
      }
      // If the character before the tracked abbreviation is '<', the user
      // started typing a raw HTML tag (e.g. <h1).  The tracker would expand
      // 'h1' to '<h1></h1>' but leave the already-typed '<', producing
      // '<<h1></h1>'.  Handle expansion ourselves, including the '<' in
      // the replacement range.
      if (tracker.range.from > 0) {
        const before = view.state.doc.sliceString(
          tracker.range.from - 1,
          tracker.range.from
        );
        if (before === '<' && tracker.preview?.startsWith('<')) {
          const from = tracker.range.from - 1; // include the '<'
          const to = tracker.range.from + tracker.abbreviation.length;
          view.dispatch({
            changes: {from, to, insert: tracker.preview},
            selection: {anchor: from + tracker.preview.length},
          });
          return true;
        }
      }
      // Reject abbreviations that overlap with {{ }} token syntax.
      // The tracker can pick up fragments of raw token markup as abbreviations.
      if (isNearTokenSyntax(view.state.doc, tracker.range)) {
        insertTab(view);
        return true;
      }
      // In markup mode, check if the expansion produces a known HTML tag.
      // This prevents CSS abbreviations (m10, te:b) from expanding to
      // meaningless tags like <m10></m10> when the editor is in HTML mode.
      // However, if the cursor is inside a CSS context (e.g. `<style>` element),
      // allow the expansion — the tracker correctly detected CSS context and
      // produced a stylesheet expansion.
      if (
        isMarkup &&
        tracker.preview &&
        !isUsefulMarkupExpansion(tracker.preview) &&
        !isCSSContextAt(view.state, view.state.selection.main.head)
      ) {
        insertTab(view);
        return true;
      }
      // In stylesheet mode, reject expansions that produce markup output.
      // This can happen when the tracker starts during the async gap between
      // a language switch and the emmet compartment reconfigure, or due to
      // the ESM/CJS module identity mismatch in isActiveAt checks.
      if (!isMarkup && tracker.preview && /^<[a-zA-Z]/.test(tracker.preview)) {
        insertTab(view);
        return true;
      }
      // Valid tracked abbreviation — let the tracker's Tab handler expand it
      return false;
    }

    // No active tracker — expand directly using the Emmet core library.
    // This handles Code Block where the abbreviation tracker's
    // htmlLanguage.isActiveAt / cssLanguage.isActiveAt checks fail
    // (ESM/CJS module identity mismatch in the bundler).
    const pos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(pos);
    const textBefore = line.text.slice(0, pos - line.from);
    const abbrMatch = textBefore.match(
      /([a-zA-Z][a-zA-Z0-9.#>+^*\[\]{}()$@!:_-]*)$/
    );
    const abbr = abbrMatch?.[1];

    if (abbr) {
      const charBefore = textBefore[textBefore.length - abbr.length - 1];

      // Detect CSS context (e.g. inside `<style>` element) using our own
      // cssLanguage import, which avoids the ESM/CJS module identity mismatch.
      // When in a CSS context within an HTML editor, expand as stylesheet/CSS
      // instead of markup/HTML so abbreviations like `m10` produce
      // `margin: 10px;` rather than `<m10></m10>`.
      const inCSSContext = isMarkup && isCSSContextAt(view.state, pos);
      const expandType = inCSSContext
        ? 'stylesheet'
        : isMarkup
          ? 'markup'
          : 'stylesheet';
      const expandSyntax = inCSSContext ? 'css' : syntax;

      try {
        const expanded = emmetCore.default(abbr, {
          type: expandType,
          syntax: expandSyntax as EmmetKnownSyntax,
          options: {'output.field': (_i: number, p: string) => p || ''},
        });
        if (expanded && expanded.length > 0) {
          // In markup mode (outside CSS context), validate the expansion
          // produces known HTML tags.  This lets Emmet aliases like "bq"
          // expand (they resolve to real tags like <blockquote>) while
          // blocking CSS abbreviations like "m10" that would produce
          // meaningless <m10></m10>.
          if (isMarkup && !inCSSContext && !isUsefulMarkupExpansion(expanded)) {
            insertTab(view);
            return true;
          }
          // In stylesheet mode (or CSS context), reject expansions that
          // produce markup — wrong expansion type.
          if ((!isMarkup || inCSSContext) && /^<[a-zA-Z]/.test(expanded)) {
            insertTab(view);
            return true;
          }
          // If the user typed '<' before the abbreviation (e.g. <h1) and
          // the expansion starts with '<', include the already-typed '<'
          // in the replacement range to avoid doubling it.
          let from = pos - abbr.length;
          if (charBefore === '<' && expanded.startsWith('<')) {
            from -= 1;
          }
          view.dispatch({
            changes: {from, to: pos, insert: expanded},
            selection: {anchor: from + expanded.length},
          });
          return true;
        }
      } catch {
        // Expansion failed — fall through
      }
    }

    // Nothing to expand — insert a tab
    insertTab(view);
    return true;
  };

  // ViewPlugin that suppresses the Emmet preview tooltip when the tracked
  // abbreviation is invalid — either it contains/abuts `{{ }}` token syntax
  // (which confuses the tracker) or its expansion would be rejected by our
  // isUsefulMarkupExpansion check (e.g. `alert` → `<alert></alert>`).
  // We toggle a CSS class on the editor and let the theme rule below hide
  // the tooltip, which is the least-invasive way to suppress it without
  // access to the tracker's internal resetTracker effect.
  const suppressBadPreview = ViewPlugin.fromClass(
    class {
      private lastPreview: string | undefined;
      private lastPreviewUseful: boolean | undefined;
      constructor(view: EditorView) {
        this.check(view);
      }
      update(update: ViewUpdate) {
        // Skip when only the viewport scrolled — tracker state only changes
        // on doc edits, selection moves, or state-effect transactions.
        if (
          !update.docChanged &&
          !update.selectionSet &&
          update.transactions.length === 0
        ) {
          return;
        }
        this.check(update.view);
      }
      check(view: EditorView) {
        const tracker = view.state.field(trackerStateField, false);
        let suppress = false;
        if (tracker && !tracker.inactive && tracker.type !== 'error') {
          // Suppress if the abbreviation or surrounding text contains token markers.
          if (isNearTokenSyntax(view.state.doc, tracker.range)) {
            suppress = true;
          }
          // In markup mode, suppress if the expansion would be rejected.
          // Cache the isUsefulMarkupExpansion result to avoid redundant
          // DOM.createElement calls when the preview string hasn't changed.
          if (
            !suppress &&
            isMarkup &&
            tracker.preview &&
            !isCSSContextAt(view.state, view.state.selection.main.head)
          ) {
            if (tracker.preview !== this.lastPreview) {
              this.lastPreview = tracker.preview;
              this.lastPreviewUseful = isUsefulMarkupExpansion(tracker.preview);
            }
            if (!this.lastPreviewUseful) {
              suppress = true;
            }
          }
        }
        // Skip the DOM write when the class already matches the desired state.
        if (
          view.dom.classList.contains('cm-suppress-emmet-preview') !== suppress
        ) {
          view.dom.classList.toggle('cm-suppress-emmet-preview', suppress);
        }
      }
    }
  );

  const suppressPreviewTheme = EditorView.baseTheme({
    '&.cm-suppress-emmet-preview .cm-tooltip:has(.emmet-preview)': {
      display: 'none',
    },
  });

  return [
    ...trackerExts,
    // Override the tracker's Tab binding (at default priority inside
    // abbreviationTracker) so that error-type abbreviations fall through
    // to insertTab instead of swallowing the keypress.
    Prec.high(keymap.of([{key: 'Tab', run: safeEmmetTab}])),
    suppressBadPreview,
    suppressPreviewTheme,
  ];
}
